interface ReorderNestedResourcesOptions<T> {
    destinationIndex: number;
    getId: (item: T) => string;
    isFixed?: (item: T) => boolean;
    orderedItems: T[];
    sourceIndex: number;
    visibleItems: T[];
    withOrder: (item: T, order: number) => T;
}

interface ReorderNestedResourcesResult<T> {
    changedItems: T[];
    moved: T;
    orderedItems: T[];
}

/**
 * Reorders the visible subset while retaining archived/hidden resources in
 * their existing slots. This keeps the tab bar and full reorder dropdown on a
 * single ordering model.
 */
export const reorderNestedResources = <T>({
    destinationIndex,
    getId,
    isFixed = () => false,
    orderedItems,
    sourceIndex,
    visibleItems,
    withOrder,
}: ReorderNestedResourcesOptions<T>): ReorderNestedResourcesResult<T> | undefined => {
    if (
        sourceIndex === destinationIndex ||
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= visibleItems.length ||
        destinationIndex >= visibleItems.length
    ) {
        return undefined;
    }

    const reorderedVisible = [...visibleItems];
    const [moved] = reorderedVisible.splice(sourceIndex, 1);

    if (isFixed(moved)) {
        return undefined;
    }

    reorderedVisible.splice(destinationIndex, 0, moved);

    const movableVisibleIds = new Set(visibleItems.filter((item) => !isFixed(item)).map(getId));
    const reorderedMovable = reorderedVisible.filter((item) => movableVisibleIds.has(getId(item)));
    let movableIndex = 0;
    const reorderedItems = orderedItems.map((item) =>
        movableVisibleIds.has(getId(item)) ? reorderedMovable[movableIndex++] : item,
    );
    const normalizedItems = reorderedItems.map((item, index) =>
        movableVisibleIds.has(getId(item)) ? withOrder(item, index) : item,
    );
    const previousIndexById = new Map(orderedItems.map((item, index) => [getId(item), index]));
    const changedItems = normalizedItems.filter((item, index) => previousIndexById.get(getId(item)) !== index);

    if (changedItems.length === 0) {
        return undefined;
    }

    return {
        changedItems,
        moved,
        orderedItems: normalizedItems,
    };
};
